import { css } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import { UniformLogo } from "src/uniform/contenttypes/logo";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { UniformColumnLayout } from "../../column-layout";

import {
    CustomerQuote,
    CustomerQuoteCarousel
} from "./customer-quote-carousel";
import { LogosMarquee } from "./logos-marquee";

export interface UniformConnectorProps {
    title: string;
    text: string;
    linkText?: string;
    linkHref?: string;
    customerQuotes: CustomerQuote[];
    mobileLogos: UniformLogo[];
    tabletLogos: UniformLogo[];
    desktopLogos: UniformLogo[];
    flattenData: boolean;
}

const UniformConnector = ({
    title,
    text,
    linkText,
    linkHref,
    customerQuotes,
    mobileLogos,
    tabletLogos,
    desktopLogos,
    flattenData = true
}: UniformConnectorProps) => {
    if (flattenData) {
        customerQuotes = flattenValues(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            customerQuotes as any
        ) as CustomerQuote[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mobileLogos = flattenValues(mobileLogos as any) as UniformLogo[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tabletLogos = flattenValues(tabletLogos as any) as UniformLogo[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        desktopLogos = flattenValues(desktopLogos as any) as UniformLogo[];
    }

    return (
        <div css={containerCss}>
            <UniformColumnLayout layout="5-7">
                <div className="contentBlock" css={lightTextCss}>
                    <h2 css={titleCss}>{title}</h2>
                    <p css={textCss}>{text}</p>
                    {linkText && linkHref && (
                        <div css={linkCss}>
                            <Link
                                aria-label={linkText}
                                text={linkText}
                                href={linkHref}
                                type="primary"
                                bgColor="bgBlackBlue"
                                dataEventProps={{
                                    "data-product-key": "uniform",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "connector"
                                }}
                            />
                        </div>
                    )}
                </div>
                <CustomerQuoteCarousel customerQuotes={customerQuotes} />
            </UniformColumnLayout>
            <LogosMarquee
                mobileLogos={mobileLogos}
                tabletLogos={tabletLogos}
                desktopLogos={desktopLogos}
            />
        </div>
    );
};

export default UniformConnector;

const containerCss = css({
    paddingBlock: token("space.1000"),
    backgroundColor: wacToken("color.primary.black"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.600")
});

const lightTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, p": {
        color: wacToken("color.neutral.light0")
    },

    [media.below.sm]: {
        textAlign: "center"
    }
});

const titleCss = css({
    marginBlockStart: token("space.400"),
    marginBlockEnd: token("space.100"),

    [media.below.sm]: {
        marginBlockStart: token("space.0")
    }
});

const textCss = css({
    marginBlock: token("space.0")
});

const linkCss = css({
    marginBlockStart: token("space.300"),
    marginBlockEnd: token("space.400"),

    [media.below.sm]: {
        marginBlockEnd: token("space.300")
    }
});
